/**
 * Light or dark, as a per-person setting.
 *
 * The choice is stored, and an inline script in app/layout.tsx applies it before the
 * first paint — the same pattern, and the same literals, as the text size (lib/os/textSize.ts).
 *
 * "system" follows the operating system and is the default.
 */

export const THEMES = ["system", "light", "dark"] as const;
export type Theme = (typeof THEMES)[number];

/** Read by the inline script in app/layout.tsx as a literal — change both together. */
export const THEME_KEY = "aura.theme";

export function isTheme(v: unknown): v is Theme {
  return typeof v === "string" && (THEMES as readonly string[]).includes(v);
}

export function readTheme(): Theme {
  if (typeof window === "undefined") return "system";
  try {
    const v = window.localStorage.getItem(THEME_KEY);
    return isTheme(v) ? v : "system";
  } catch {
    return "system";
  }
}

export function resolvesDark(theme: Theme): boolean {
  if (theme === "dark") return true;
  if (theme === "light") return false;
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** Apply it and remember it. */
export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolvesDark(theme));
  try {
    window.localStorage.setItem(THEME_KEY, theme);
  } catch {
    // Storage blocked: the choice holds for this page and is lost on reload, which is the best
    // a browser without site data can do.
  }
}

/**
 * While the choice is "Match system", keep the class right when the OS flips (the boot script only
 * sets it for the first paint). Returns the unsubscribe, so it can be an effect as it stands.
 */
export function followSystemTheme(): (() => void) | undefined {
  if (readTheme() !== "system") return;
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const follow = () => document.documentElement.classList.toggle("dark", mq.matches);
  mq.addEventListener("change", follow);
  return () => mq.removeEventListener("change", follow);
}

/** The inline, pre-paint version. Kept here so the literal lives next to the key it reads. */
export const THEME_BOOT_SCRIPT =
  "try{var t=localStorage.getItem('aura.theme');var d=t==='dark'||(t!=='light'&&matchMedia('(prefers-color-scheme: dark)').matches);document.documentElement.classList.toggle('dark',d)}catch(e){}";
